import { useState, useEffect } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { BudgetV2Dto } from '@cloudcost/shared-types/budgets';
import { urls } from '@/urls';
import { SystemData } from '../../calculations';

type BudgetsBulkResponse = {
  systems: Array<{ systemId: number; budget: BudgetV2Dto | null }>;
};

export interface ChartItem {
  name: string;
  spending: number;
  value: number;
}

export const useSystems = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const [items, setItems] = useState<SystemData[]>([]);
  const [loading, setLoading] = useState(enabled);

  const fetchSystemsData = async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    try {
      const systemsData = (await fetchWrapper.get(
        urls.DevOps.allsystems,
      )) as SystemData[];

      // Enrich systems with budgets from v2 (GET-only, no client-side "calculation")
      const budgetBySystemId = new Map<number, BudgetV2Dto>();
      try {
        const bulk = (await fetchWrapper.post(urls.DevOps.getLatestBudgetsV2Bulk, {
          systemIds: systemsData.map((s) => s.id),
        })) as BudgetsBulkResponse;
        (bulk.systems ?? [])
          .filter((x) => x.budget)
          .forEach((x) => {
            budgetBySystemId.set(x.systemId, x.budget as BudgetV2Dto);
          });
      } catch (e) {
        console.warn('[useSystems] Failed to fetch budgets bulk, falling back to per-system calls', e);
        const budgetResults = await Promise.all(
          systemsData.map(async (s) => {
            try {
              const budget = (await fetchWrapper.get(
                urls.DevOps.getLatestBudgetV2ForScope({ systemId: s.id }),
              )) as BudgetV2Dto;
              return { systemId: s.id, budget };
            } catch {
              return { systemId: s.id, budget: null as BudgetV2Dto | null };
            }
          }),
        );
        budgetResults
          .filter((r) => r.budget)
          .forEach((r) => budgetBySystemId.set(r.systemId, r.budget!));
      }

      const enriched = systemsData.map((s) => {
        const b = budgetBySystemId.get(s.id);
        if (!b) return s;
        return {
          ...s,
          value: Number(b.yearly_limit),
          original_currency_value: Number(b.yearly_limit),
          original_currency: b.currency,
          budget_id: b.budget_id,
        };
      });

      setItems(enriched);
    } catch (error) {
      console.error('Error fetching systems data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchSystemsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const refreshData = () => {
    return fetchSystemsData();
  };

  return {
    items,
    loading,
    refreshData,
    setItems,
  };
};
